import React from 'react'

import { CopyIcon } from 'lucide-react'
import { makeObservable, observable } from 'mobx'
import { inject, observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import { Button } from '~/client/src/shared/components/Button/Button'
import { Loader } from '~/client/src/shared/components/Loader'
import MenuCloser from '~/client/src/shared/components/MenuCloser'
import StruxhubInput from '~/client/src/shared/components/StruxhubInputs/StruxhubInput'
import StruxhubSelect from '~/client/src/shared/components/StruxhubInputs/StruxhubSelect'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import EventsStore from '~/client/src/shared/stores/EventStore/Events.store'

import MapViewItemsSetupStore from '../../../stores/MapViewItemsSetup.store'
import SitemapControlStore from '../../../stores/SitemapControl.store'
import { UploadRemoteTilesetDialogStore } from './UploadRemoteTilesetDialog.store'
import { WMTSDimension } from './wmtsCapabilities'

import './UploadRemoteTilesetDialog.scss'

// String constants for labels and messages
const LABEL_UPLOAD_TILESET = 'Upload WMTS Tileset'
const LABEL_CONFIGURATION = 'Configuration'
const LABEL_WMTS_URL = 'WMTS Url'
const LABEL_NAME = 'Name'
const LABEL_FINALIZED_URL = 'Finalized Url'
const LABEL_SERVICE_INFORMATION = 'Service Information'
const LABEL_VERSION = 'Version'
const LABEL_SELECT_LAYER = 'Select Layer'
const LABEL_FORMAT = 'Format'
const LABEL_TILE_MATRIX_SET = 'Tile Matrix Set'
const LABEL_RESOURCE_URL_TEMPLATE = 'Resource URL Template'
const LABEL_STYLE = 'Style'
const LABEL_LAYER_INFORMATION = 'Layer Information:'
const LABEL_IDENTIFIER = 'Identifier:'
const LABEL_TITLE = 'Title:'
const LABEL_FORMATS = 'Formats:'
const LABEL_TILE_MATRIX_SETS = 'TileMatrixSets:'
const LABEL_RESOURCE_URLS = 'Resource URLs:'
const LABEL_STYLES = 'Styles:'
const LABEL_DIMENSIONS = 'Dimensions:'
const LABEL_AVAILABLE_DIMENSIONS = 'Available Dimensions:'
const LABEL_ABSTRACT = 'Abstract:'
const LABEL_DEFAULT = 'Default'
const LABEL_USE_KVP_STYLE_URL = 'Use KVP Style URL'
const LABEL_TEMPLATE = 'template(s)'
const LABEL_NONE = 'None for selected template'
const MSG_ENTER_VALID_WMTS_URL =
  'Enter a valid WMTS URL to see service information'
const MSG_UNABLE_TO_DETECT_WMTS =
  'Unable to detect WMTS service or fetch capabilities'
const MSG_LOADING_CAPABILITIES = 'Loading capabilities...'

interface IProps {
  store: SitemapControlStore
  shouldFixTopPosition?: boolean
  mapViewItemsSetupStore?: MapViewItemsSetupStore
  eventsStore?: EventsStore
}

@inject('eventsStore')
@observer
export default class UploadWMTSDialog extends React.Component<IProps> {
  @observable
  private store: UploadRemoteTilesetDialogStore = null

  public constructor(props: IProps) {
    super(props)
    this.store = new UploadRemoteTilesetDialogStore(props.eventsStore)
    makeObservable(this)
  }

  public componentWillUnmount(): void {
    this.store.cleanup()
  }

  private renderServiceInfo(): JSX.Element | null {
    const { wmtsCapabilities } = this.store

    if (!wmtsCapabilities) {
      return (
        <div className="wmts-info-placeholder">
          <div className="text size12 gray center pa20">
            {MSG_ENTER_VALID_WMTS_URL}
          </div>
        </div>
      )
    }

    return (
      <div className="service-info mb15 pa10">
        <div className="text bold size14">
          {wmtsCapabilities.service?.title}
        </div>
        {wmtsCapabilities.service?.abstract && (
          <div className="text size12 gray mt5">
            {wmtsCapabilities.service.abstract}
          </div>
        )}
        <div className="text size11 gray mt5">
          {LABEL_VERSION}: {wmtsCapabilities.version}
        </div>
      </div>
    )
  }

  private renderCapabilitiesInfo() {
    const { wmtsCapabilities, detectedServiceType, isLoadingCapabilities } =
      this.store

    if (isLoadingCapabilities) {
      return (
        <div className="wmts-info-placeholder">
          <div className="full-height col x-center my10">
            <Loader />
            <div className="text size12 gray mt10">
              {MSG_LOADING_CAPABILITIES}
            </div>
          </div>
        </div>
      )
    }

    if (!wmtsCapabilities) {
      return (
        <div className="wmts-info-placeholder">
          <div className="text size12 gray center pa20">
            {detectedServiceType === 'Unknown'
              ? MSG_UNABLE_TO_DETECT_WMTS
              : MSG_ENTER_VALID_WMTS_URL}
          </div>
        </div>
      )
    }

    return (
      <div className="capabilities-info">
        {this.renderServiceInfo()}
        {this.renderWMTSInfo()}
      </div>
    )
  }

  private renderWMTSInfo() {
    const {
      wmtsCapabilities,
      selectedLayerIndex,
      selectedFormat,
      selectedTileMatrixSet,
      selectedResourceUrl,
      selectedStyle,
      selectedDimensions,
      handleLayerChange,
      handleFormatChange,
      handleTileMatrixSetChange,
      handleResourceUrlChange,
      handleStyleChange,
      handleDimensionChange,
    } = this.store

    const { layers } = wmtsCapabilities
    const selectedLayer = layers[selectedLayerIndex]
    const formatOptions = selectedLayer?.formats || []
    const tileMatrixSetOptions = selectedLayer?.tileMatrixSetLinks || []
    const resourceUrlOptions = selectedLayer?.resourceUrls || []
    const styleOptions = selectedLayer?.styles || []
    const dimensionOptions = selectedLayer?.dimensions || []

    // Helper function to check if dimension should be shown
    const shouldShowDimension = (dimension: WMTSDimension): boolean => {
      // If no ResourceURL is selected, show all dimensions (for KVP style)
      if (!selectedResourceUrl) {
        return true
      }

      // Check if the selected ResourceURL template contains this dimension
      const dimensionPatterns = [
        `{${dimension.identifier}}`,
        `{${dimension.identifier.toUpperCase()}}`,
        `{${dimension.identifier.toLowerCase()}}`,
        // Handle common dimension aliases
        ...(dimension.identifier.toLowerCase() === 'time'
          ? ['{Time}', '{TIME}', '{time}', '{TimeStart}', '{TimeEnd}']
          : []),
        ...(dimension.identifier.toLowerCase() === 'elevation'
          ? ['{Elevation}', '{ELEVATION}', '{elevation}']
          : []),
        ...(dimension.identifier.toLowerCase() === 'band'
          ? ['{Band}', '{BAND}', '{band}']
          : []),
      ]

      return dimensionPatterns.some(pattern =>
        selectedResourceUrl.includes(pattern),
      )
    }

    // Filter dimensions based on selected ResourceURL
    const filteredDimensionOptions =
      dimensionOptions.filter(shouldShowDimension)

    // Enhanced helper function to format dimension values for display
    const formatDimensionValue = (
      value: string,
      dimension: WMTSDimension,
    ): string => {
      // For time dimensions, try to format ISO dates nicely
      if (
        dimension.uom === 'ISO8601' ||
        dimension.identifier.toLowerCase() === 'time'
      ) {
        // Handle range formats like "2023-06-01/2023-06-10/P1D"
        if (value.includes('/')) {
          const parts = value.split('/')
          if (parts.length >= 2) {
            const startDate = formatSingleDate(parts[0])
            const endDate = formatSingleDate(parts[1])
            const period = parts[2] ? ` (${formatPeriod(parts[2])})` : ''

            if (parts[0] === parts[1]) {
              // Single date range like "2025-04-08/2025-04-08/P1M"
              return `${startDate}`
            } else {
              return `${startDate} to ${endDate}${period}`
            }
          }
        }
        // Try to format single dates
        return formatSingleDate(value)
      }
      return value
    }

    // Helper to format single dates
    const formatSingleDate = (dateStr: string): string => {
      try {
        const date = new Date(dateStr)
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString()
        }
      } catch {
        // Fall back to original value
      }
      return dateStr
    }

    // Helper to format ISO 8601 periods
    const formatPeriod = (period: string): string => {
      const periodMap: { [key: string]: string } = {
        P1D: 'Daily',
        P1W: 'Weekly',
        P1M: 'Monthly',
        P1Y: 'Yearly',
        PT1H: 'Hourly',
        PT1M: 'Per minute',
      }
      return periodMap[period] || period
    }

    // Helper function to get dimension label
    const getDimensionLabel = (dimension: WMTSDimension): string => {
      let label = dimension.title || dimension.identifier
      if (dimension.uom) {
        label += ` (${dimension.uom})`
      }
      return label
    }

    // Helper to show time range information
    const getTimeRangeInfo = (value: string): string | null => {
      if (value.includes('/')) {
        const parts = value.split('/')
        if (parts.length >= 2 && parts[0] !== parts[1]) {
          return `Start: ${formatSingleDate(parts[0])}, End: ${formatSingleDate(
            parts[1],
          )}`
        }
      }
      return null
    }

    return (
      <>
        <div className="layer-selector mb15">
          <StruxhubSelect
            value={String(selectedLayerIndex)}
            onChange={handleLayerChange}
            label={LABEL_SELECT_LAYER}
            isRequired={true}
          >
            {layers.map((layer, idx) => (
              <option key={layer.identifier || idx} value={idx}>
                {layer.title || layer.identifier || `Layer ${idx + 1}`}
              </option>
            ))}
          </StruxhubSelect>
        </div>

        {formatOptions.length > 0 && (
          <div className="format-selector mb15">
            <StruxhubSelect
              value={selectedFormat}
              onChange={handleFormatChange}
              label={LABEL_FORMAT}
            >
              {formatOptions.length > 1 && (
                <option value="">
                  {LABEL_DEFAULT} ({formatOptions[0]})
                </option>
              )}
              {formatOptions.map(fmt => (
                <option key={fmt} value={fmt}>
                  {fmt}
                </option>
              ))}
            </StruxhubSelect>
          </div>
        )}

        {tileMatrixSetOptions.length > 0 && (
          <div className="tilematrixset-selector mb15">
            <StruxhubSelect
              value={selectedTileMatrixSet}
              onChange={handleTileMatrixSetChange}
              label={LABEL_TILE_MATRIX_SET}
            >
              {tileMatrixSetOptions.length > 1 && (
                <option value="">
                  {LABEL_DEFAULT} ({tileMatrixSetOptions[0]})
                </option>
              )}
              {tileMatrixSetOptions.map(tms => (
                <option key={tms} value={tms}>
                  {tms}
                </option>
              ))}
            </StruxhubSelect>
          </div>
        )}

        {resourceUrlOptions.length > 0 && (
          <div className="resourceurl-selector mb15">
            <StruxhubSelect
              value={selectedResourceUrl}
              onChange={handleResourceUrlChange}
              label={LABEL_RESOURCE_URL_TEMPLATE}
            >
              <option value="">{LABEL_USE_KVP_STYLE_URL}</option>
              {resourceUrlOptions
                .filter(ru => ru.resourceType === 'tile')
                .map((ru, idx) => (
                  <option key={ru.template || idx} value={ru.template}>
                    {ru.resourceType} - {ru.format}
                    {ru.template.includes('{Time}') ? ' (with Time)' : ''}
                    {ru.template.includes('{TimeStart}') ||
                    ru.template.includes('{TimeEnd}')
                      ? ' (with TimeStart/End)'
                      : ''}
                  </option>
                ))}
            </StruxhubSelect>
          </div>
        )}

        {styleOptions.length > 0 && (
          <div className="style-selector mb15">
            <StruxhubSelect
              value={selectedStyle}
              onChange={handleStyleChange}
              label={LABEL_STYLE}
            >
              {styleOptions.length > 1 && (
                <option value="">
                  {LABEL_DEFAULT} (
                  {styleOptions.find(s => s.isDefault)?.identifier ||
                    styleOptions[0]?.identifier ||
                    'default'}
                  )
                </option>
              )}
              {styleOptions.map(style => (
                <option key={style.identifier} value={style.identifier}>
                  {style.identifier} {style.isDefault ? '(Default)' : ''}
                </option>
              ))}
            </StruxhubSelect>
          </div>
        )}

        {/* Dimension Selectors - Only show if dimension is in selected ResourceURL */}
        {filteredDimensionOptions.map(dimension => (
          <div key={dimension.identifier} className="dimension-selector mb15">
            <StruxhubSelect
              value={selectedDimensions[dimension.identifier] || ''}
              onChange={event =>
                handleDimensionChange(dimension.identifier, event.target.value)
              }
              label={getDimensionLabel(dimension)}
            >
              <option value="">
                {LABEL_DEFAULT} (
                {dimension.default
                  ? formatDimensionValue(dimension.default, dimension)
                  : 'None'}
                )
              </option>
              {dimension.values.map(value => (
                <option
                  key={value}
                  value={value}
                  title={getTimeRangeInfo(value) || undefined}
                >
                  {formatDimensionValue(value, dimension)}
                </option>
              ))}
            </StruxhubSelect>

            {/* Show additional info for selected time ranges */}
            {dimension.identifier.toLowerCase() === 'time' &&
              selectedDimensions[dimension.identifier] &&
              getTimeRangeInfo(selectedDimensions[dimension.identifier]) && (
                <div className="text size10 gray mt5 pl5">
                  {getTimeRangeInfo(selectedDimensions[dimension.identifier])}
                </div>
              )}
          </div>
        ))}

        {selectedLayer && (
          <div className="wmts-layer-info mt15 pa10">
            <div className="text bold size12 mb10">
              {LABEL_LAYER_INFORMATION}
            </div>
            <div className="text size11 mb5">
              <b>{LABEL_IDENTIFIER}</b> {selectedLayer.identifier || '-'}
            </div>
            <div className="text size11 mb5">
              <b>{LABEL_TITLE}</b> {selectedLayer.title || '-'}
            </div>
            <div className="text size11 mb5">
              <b>{LABEL_FORMATS}</b> {selectedLayer.formats?.join(', ') || '-'}
            </div>
            <div className="text size11 mb5">
              <b>{LABEL_TILE_MATRIX_SETS}</b>{' '}
              {selectedLayer.tileMatrixSetLinks?.join(', ') || '-'}
            </div>
            <div className="text size11 mb5">
              <b>{LABEL_RESOURCE_URLS}</b>{' '}
              {selectedLayer.resourceUrls?.length || 0} {LABEL_TEMPLATE}
            </div>
            <div className="text size11 mb5">
              <b>{LABEL_STYLES}</b>{' '}
              {selectedLayer.styles?.map(s => s.identifier).join(', ') || '-'}
            </div>
            <div className="text size11 mb5">
              <b>{LABEL_DIMENSIONS}</b>{' '}
              {selectedLayer.dimensions?.map(d => d.identifier).join(', ') ||
                '-'}
            </div>
            <div className="text size11 mb5">
              <b>{LABEL_AVAILABLE_DIMENSIONS}</b>{' '}
              {filteredDimensionOptions.length > 0
                ? filteredDimensionOptions.map(d => d.identifier).join(', ')
                : LABEL_NONE}
            </div>
            {selectedLayer.abstract && (
              <div className="text size11">
                <b>{LABEL_ABSTRACT}</b> {selectedLayer.abstract}
              </div>
            )}
          </div>
        )}
      </>
    )
  }

  public render(): JSX.Element {
    const { shouldFixTopPosition } = this.props
    const { isUploading, toggleCreateRemoteTilesetMenu, saveRemoteTileset } =
      this.props.store
    const {
      remoteTileset,
      remoteTilesetInput,
      isSaveButtonActive,
      resLink,
      hasSomeError,
      hasSomeWarning,
      errorText,
      warningText,
      updateName,
      updateUrl,
      copyText,
    } = this.store

    return (
      <MenuCloser
        className={classList({
          'absolute map-upload-block remote-tileset-block wmts-dialog-two-column': true,
          'fixed-top': shouldFixTopPosition,
        })}
        closeMenu={toggleCreateRemoteTilesetMenu}
      >
        {/* Header - Fixed Height */}
        <div className="wmts-dialog-header">
          <div className="text size24 center">{LABEL_UPLOAD_TILESET}</div>
        </div>

        {/* Content - Flexible Height with Scroll */}
        <div className="wmts-dialog-content">
          {/* Left Column - Input Fields */}
          <div className="wmts-dialog-left-column">
            <div className="text size16 bold mb15">{LABEL_CONFIGURATION}</div>
            <StruxhubInput
              label={LABEL_WMTS_URL}
              isRequired={true}
              onChange={updateUrl}
              value={remoteTileset.source}
              withDelay={true}
            />
            <StruxhubInput
              label={LABEL_NAME}
              isRequired={true}
              onChange={updateName}
              value={remoteTileset.name}
            />

            <div className="mb15">
              <div className="row">
                <StruxhubInput
                  label={LABEL_FINALIZED_URL}
                  isRequired={true}
                  isReadOnly={true}
                  value={resLink}
                />
                <Button
                  variant="ghost"
                  size="icon-sm"
                  className="tw:flex-none! tw:mx-2.5"
                  aria-label={Localization.translator.copy}
                  onClick={copyText}
                >
                  <CopyIcon />
                </Button>
              </div>
            </div>
          </div>

          {/* Right Column - WMTS Information */}
          <div className="wmts-dialog-right-column">
            <div className="text size16 bold mb15">
              {LABEL_SERVICE_INFORMATION}
            </div>
            {this.renderCapabilitiesInfo()}
          </div>
        </div>

        {/* Footer - Fixed Height */}
        <div className="wmts-dialog-footer col x-center">
          {/* Error Message */}
          {hasSomeWarning && (
            <div className="text orange bold center mb5">{warningText}</div>
          )}
          {hasSomeError && (
            <div className="text red bold center">{errorText}</div>
          )}
          {/* Save Button */}
          <div
            className={classList({
              'submit-button light pa10 wmts-save-button mt15': true,
              'inactive-element unclickable-element': !isSaveButtonActive,
            })}
            onClick={saveRemoteTileset.bind(this, remoteTilesetInput)}
          >
            {isUploading ? <Loader /> : Localization.translator.save}
          </div>
        </div>
      </MenuCloser>
    )
  }
}
